/**
 * credits.js — the open-source components BisaWofa is built on.
 *
 * Single source of truth for attribution. The Credits modal renders this, and
 * the Word document (docs/BisaWofa-Open-Source-Credits.docx) is generated from
 * the same data, so the two can't drift apart.
 *
 * Keeping this accurate is a licence obligation, not a courtesy: the MIT and
 * BSD licences here require the copyright notice to be reproduced in software
 * we distribute, and OpenStreetMap's ODbL requires visible source credit.
 *
 * When adding a dependency, add it here too — and record the version actually
 * installed (`npm ls <pkg>`), not the semver range from package.json.
 *
 * @module credits
 */

/**
 * @typedef {object} Component
 * @property {string} name
 * @property {string} version
 * @property {string} license      SPDX identifier
 * @property {string} url          project homepage
 * @property {string} use          what BisaWofa uses it for, in plain words
 */

/** Licence summaries, in plain language. Keyed by SPDX id. */
export const LICENSES = {
  'MIT': 'Permissive. Free to use, modify and distribute; keep the copyright notice.',
  'BSD-2-Clause': 'Permissive. Free to use and distribute; keep the copyright notice and disclaimer.',
  'BSD-3-Clause': 'Permissive. As BSD-2, and the project name may not be used to endorse our product.',
  'Apache-2.0': 'Permissive. Includes an express patent grant; state any changes made.',
  'ISC': 'Permissive, equivalent to MIT in effect.',
  'LGPL-2.1-only': 'Copyleft (library). Free to use as an unmodified library; changes to the library itself must be published under the same licence.',
  'ODbL-1.0': 'Share-alike for data. Credit the source; a derived database must be shared alike.',
  'CC-BY-4.0': 'Free to use with attribution to the author.',
  'Public Domain': 'No restrictions on use.',
  'Public Domain (Unlicense)': 'Released into the public domain; no conditions on use.',
  'PostgreSQL License': 'Permissive, similar to MIT/BSD. Free to use for any purpose; keep the copyright notice.',
  'GPL-2.0-or-later': 'Copyleft. Used as a separate database server we communicate with, not linked into our code, so it places no obligation on BisaWofa.',
  'Esri terms of use': 'Commercial service, not open source. Use is governed by Esri\'s own terms; credit "Tiles © Esri".',
  'OASIS Standard': 'An open specification, free to implement. Not software.',
  'W3C Recommendation': 'An open web standard, free to implement. Not software.',
};

/** Front-end libraries bundled into the app. */
export const APP_COMPONENTS = [
  { name: 'OpenLayers', version: '10.7.0', license: 'BSD-2-Clause', url: 'https://openlayers.org/',
    use: 'The map engine — rendering, layers, projections and all map interaction.' },
  { name: 'ol-ext', version: '4.0.37', license: 'BSD-3-Clause', url: 'https://viglino.github.io/ol-ext/',
    use: 'Extra OpenLayers controls, including the place search box.' },
  { name: 'Bootstrap', version: '5.3.8', license: 'MIT', url: 'https://getbootstrap.com/',
    use: 'Layout grid, side panels (offcanvas) and base UI components.' },
  { name: 'Bootstrap Icons', version: '1.13.1', license: 'MIT', url: 'https://icons.getbootstrap.com/',
    use: 'The icon set used across the interface and the radial menu.' },
  { name: 'Popper.js', version: '2.11.8', license: 'MIT', url: 'https://popper.js.org/',
    use: 'Positioning engine behind Bootstrap dropdowns and tooltips.' },
  { name: 'SQLocal', version: '0.16.0', license: 'MIT', url: 'https://sqlocal.dev/',
    use: 'Runs a real SQL database inside the browser so the app works offline.' },
  { name: 'SQLite Wasm', version: '3.50.4', license: 'Apache-2.0', url: 'https://sqlite.org/wasm/',
    use: 'The SQLite database engine itself, compiled to WebAssembly (used by SQLocal).' },
  { name: 'coincident', version: '1.2.3', license: 'ISC', url: 'https://github.com/WebReflection/coincident',
    use: 'Worker communication helper used by SQLocal.' },
];

/** Native app shell and device plugins (iOS / Android builds). */
export const NATIVE_COMPONENTS = [
  { name: 'Capacitor', version: '8.3.1', license: 'MIT', url: 'https://capacitorjs.com/',
    use: 'Wraps the web app as a native iOS and Android app.' },
  { name: '@capacitor-community/bluetooth-le', version: '8.1.3', license: 'MIT', url: 'https://github.com/capacitor-community/bluetooth-le',
    use: 'Bluetooth Low Energy access for the offline mesh messaging.' },
  { name: '@capacitor-community/speech-recognition', version: '7.0.1', license: 'MIT', url: 'https://github.com/capacitor-community/speech-recognition',
    use: 'Voice-to-text input on phones, where the browser API is unavailable.' },
];

/** Server-side components. */
export const SERVER_COMPONENTS = [
  { name: 'PostgreSQL', version: '16', license: 'PostgreSQL License', url: 'https://www.postgresql.org/',
    use: 'The central database behind the shared map data.' },
  { name: 'PostGIS', version: '3', license: 'GPL-2.0-or-later', url: 'https://postgis.net/',
    use: 'Geographic extension to PostgreSQL for spatial queries.' },
];

/** Build tooling (not shipped in the app, but part of how it is made). */
export const BUILD_COMPONENTS = [
  { name: 'Vite', version: '6.4.1', license: 'MIT', url: 'https://vite.dev/',
    use: 'Development server and production build tool.' },
];

/**
 * External data and map services. These are not code we ship — they are
 * services we call — but most require visible credit, so they belong here.
 */
export const DATA_SERVICES = [
  { name: 'OpenStreetMap', license: 'ODbL-1.0', url: 'https://www.openstreetmap.org/copyright',
    use: 'Base map tiles and the points of interest (fuel stations, bus and taxi stops).',
    note: 'Credit is mandatory: "© OpenStreetMap contributors".' },
  { name: 'OpenTopoMap', license: 'CC-BY-4.0', url: 'https://opentopomap.org/',
    use: 'The topographic base map option.',
    note: 'Map data © OpenStreetMap contributors, rendering © OpenTopoMap.' },
  { name: 'CARTO basemaps', license: 'CC-BY-4.0', url: 'https://carto.com/attributions',
    use: 'The Light and Dark base map styles.',
    note: 'Credit: "© CARTO", plus OpenStreetMap contributors.' },
  { name: 'Esri World Imagery', license: 'Esri terms of use', url: 'https://www.esri.com/en-us/legal/terms/full-master-agreement',
    use: 'The satellite imagery base map option.',
    note: 'Credit: "Tiles © Esri". Not open source — used under Esri\'s terms.' },
  { name: 'OSRM (Open Source Routing Machine)', license: 'BSD-2-Clause', url: 'https://project-osrm.org/',
    use: 'Calculates driving directions to a selected place.',
    note: 'The public demo server is for light use only; self-host for production.' },
  { name: 'Ghana Meteorological Agency', license: 'Public Domain', url: 'https://www.meteo.gov.gh/',
    use: 'Official weather alerts, via the agency\'s public CAP feed.',
    note: 'The feed is published as public domain.' },
];

/** Protocols and specifications the app implements (no code reused). */
export const STANDARDS = [
  { name: 'Bitchat protocol', license: 'Public Domain (Unlicense)', url: 'https://github.com/permissionlesstech/bitchat',
    use: 'The offline Bluetooth mesh messaging format, implemented independently for compatibility.' },
  { name: 'ActivityPub', license: 'W3C Recommendation', url: 'https://www.w3.org/TR/activitypub/',
    use: 'The planned open standard for social features and federation.' },
  { name: 'Common Alerting Protocol (CAP 1.2)', license: 'OASIS Standard', url: 'https://docs.oasis-open.org/emergency/cap/v1.2/CAP-v1.2-os.html',
    use: 'The international emergency-alert format used for the weather alerts.' },
];

/** All component groups, in display order. */
export const CREDIT_SECTIONS = [
  { key: 'app',    title: 'Application libraries', icon: 'bi-window-stack',   items: APP_COMPONENTS },
  { key: 'native', title: 'Mobile app components', icon: 'bi-phone',          items: NATIVE_COMPONENTS },
  { key: 'server', title: 'Server components',     icon: 'bi-hdd-rack',       items: SERVER_COMPONENTS },
  { key: 'build',  title: 'Build tools',           icon: 'bi-tools',          items: BUILD_COMPONENTS },
  { key: 'data',   title: 'Map & data services',   icon: 'bi-globe-americas', items: DATA_SERVICES },
  { key: 'std',    title: 'Standards & protocols', icon: 'bi-diagram-3',      items: STANDARDS },
];

/** Total number of credited components. */
export function creditCount() {
  return CREDIT_SECTIONS.reduce((n, s) => n + s.items.length, 0);
}

export default { CREDIT_SECTIONS, LICENSES, creditCount };
